import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useMatches } from '@tanstack/react-router'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { QuoteAssistantMount } from '@/features/quote-builder/shell/assistant-mount'
import { PracticeBanner, PracticeCoach } from '@/features/learn/practice/practice-coach'
import { usePracticeSession } from '@/features/learn/practice/use-practice-session'
import { cn } from '@/lib/utils'

type DashboardLayoutProps = {
  children: ReactNode
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const mainRef = useRef<HTMLElement>(null)
  const { pathname } = useLocation()
  const phone = useMediaQuery(PHONE_QUERY)
  const fullScreenPage = useMatches({ select: (matches) => matches.some((match) => match.staticData?.phoneFullScreen) })
  // An open chat on a phone is the whole screen, like a messaging app: its own header has the way back.
  const fullScreen = phone && fullScreenPage
  // Room below the page for the practice panel, so it never hides the last controls.
  const practising = usePracticeSession() !== null

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0)
  }, [pathname])

  return (
    <div className="flex h-dvh max-h-dvh overflow-clip">
      <Sidebar open={sidebarOpen} onOpenChange={setSidebarOpen} />
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-clip">
        <PracticeBanner />
        {fullScreen ? (
          <main ref={mainRef} className="min-h-0 flex-1 overflow-hidden bg-background">
            {children}
          </main>
        ) : (
          <>
            <Header onMenuClick={() => setSidebarOpen(true)} />
            <main
              ref={mainRef}
              className="min-h-0 flex-1 overscroll-y-contain overflow-y-auto overflow-x-hidden bg-[#fafafa] p-4 md:p-6"
            >
              <div className={cn('min-h-full min-w-0', practising ? 'pb-64' : 'pb-24')}>
                {children}
              </div>
              <footer className="pb-8 pt-4 text-center text-xs text-muted-foreground">
                &copy; {new Date().getFullYear()} USA Restaurant Suppliers. All rights reserved.
              </footer>
            </main>
          </>
        )}
      </div>
      <QuoteAssistantMount />
      <PracticeCoach />
    </div>
  )
}
